import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, ChevronRight, Hash, Users, BookOpen, Calendar, Settings, BarChart3, Home, FileText, Megaphone } from 'lucide-react';
import api from '../services/api';
import useAuthStore from '../store/authStore';
import { trackEvent } from '../lib/telemetry';

/**
 * CommandPalette — Phase 3.1
 *
 * Global, keyboard-first launcher. Cmd/Ctrl+K opens it from anywhere.
 *
 * What it does:
 *   - Static page jumps (Dashboard, Students, Lessons, Calendar, …)
 *   - Action shortcuts (Mark attendance for next lesson, New survey, …)
 *   - Live entity search: students + groups (debounced 200ms)
 *
 * What it does NOT do (yet):
 *   - Recent-items pinning (persist in localStorage — Phase 3.2)
 *   - Fuzzy matching (string-includes is enough for now; revisit when the
 *     entity catalog grows past ~500 items per type)
 */

const SECTION_ORDER = ['actions', 'pages', 'students', 'groups'];

const SECTION_LABELS = {
  actions:  'Actions',
  pages:    'Pages',
  students: 'Students',
  groups:   'Groups',
};

function fuzzyContains(haystack, needle) {
  if (!needle) return true;
  return String(haystack || '').toLowerCase().includes(needle.toLowerCase());
}

export default function CommandPalette() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token, centerType, user } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [entities, setEntities] = useState({ students: [], groups: [] });
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Cmd/Ctrl+K — global open shortcut
  useEffect(() => {
    const onKey = (e) => {
      const isPaletteShortcut =
        (e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey);
      if (isPaletteShortcut) {
        e.preventDefault();
        setOpen((o) => {
          if (!o) trackEvent('palette.open', { trigger: 'shortcut' });
          return !o;
        });
        return;
      }
      if (open && e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Focus input + reset on each open
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Load students/groups index for search. Cached for the session.
  useEffect(() => {
    if (!open || !token) return;
    if (entities.students.length > 0 || entities.groups.length > 0) return;
    let cancelled = false;
    (async () => {
      try {
        const [s, g] = await Promise.all([
          api.get('/students?limit=100000&enrich=0').catch(() => []),
          api.get('/groups').catch(() => []),
        ]);
        if (cancelled) return;
        const studentsArr = Array.isArray(s) ? s : (s?.data || []);
        const groupsArr = Array.isArray(g) ? g : (g?.data || []);
        setEntities({ students: studentsArr, groups: groupsArr });
      } catch { /* soft-fail */ }
    })();
    return () => { cancelled = true; };
  }, [open, token, entities.students.length, entities.groups.length]);

  const isLearning = centerType === 'learning';
  const isAdmin = user?.role !== 'teacher';

  const pages = useMemo(() => {
    const base = [
      { id: 'page-dashboard', label: t('sidebar.dashboard', 'Dashboard'), icon: Home, path: '/dashboard' },
      { id: 'page-calendar',  label: t('sidebar.calendar',  'Calendar'),  icon: Calendar, path: '/calendar' },
      { id: 'page-students',  label: t('sidebar.students',  'Students'),  icon: Users,    path: '/students' },
      { id: 'page-surveys',   label: t('sidebar.surveys',   'Surveys'),   icon: FileText, path: '/surveys' },
      { id: 'page-settings',  label: t('sidebar.settings',  'Settings'),  icon: Settings, path: '/settings' },
      { id: 'page-account',   label: t('sidebar.account',   'Account'),   icon: Users,    path: '/account' },
    ];
    if (isLearning) {
      base.push(
        { id: 'page-lessons',     label: t('sidebar.lessons',    'Lessons'),    icon: BookOpen,  path: '/lessons' },
        { id: 'page-groups',      label: t('sidebar.groups',     'Groups'),     icon: Users,     path: '/groups' },
        { id: 'page-teachers',    label: t('sidebar.teachers',   'Teachers'),   icon: Users,     path: '/teachers' },
        { id: 'page-jibaya',      label: t('sidebar.jibaya',     'Jibaya'),     icon: BarChart3, path: '/jibaya' },
        { id: 'page-analytics',   label: t('sidebar.analytics',  'Analytics'),  icon: BarChart3, path: '/analytics' },
        { id: 'page-reports',     label: t('sidebar.reports',    'Reports'),    icon: FileText,  path: '/reports' },
      );
    }
    if (isAdmin) {
      base.push(
        { id: 'page-bulk-send',  label: t('sidebar.bulkSend', 'WhatsApp bulk send'), icon: Megaphone, path: '/whatsapp/bulk-send' },
        { id: 'page-audit-log',  label: t('sidebar.auditLog', 'Audit log'),          icon: FileText,  path: '/audit-log' },
      );
    }
    return base;
  }, [t, isLearning, isAdmin]);

  const actions = useMemo(() => [
    {
      id: 'act-attendance-today',
      label: t('palette.markAttendanceToday', 'Mark attendance for today'),
      icon: BookOpen,
      path: '/lessons',
    },
    {
      id: 'act-new-lesson',
      label: t('palette.newLesson', 'New lesson'),
      icon: BookOpen,
      path: '/lessons?new=1',
    },
    {
      id: 'act-new-survey',
      label: t('palette.newSurvey', 'New survey'),
      icon: FileText,
      path: '/surveys?new=1',
    },
  ], [t]);

  const matchingStudents = useMemo(() => {
    if (!query) return entities.students.slice(0, 5);
    return entities.students
      .filter(s => fuzzyContains(`${s.firstName || ''} ${s.lastName || ''} ${s.phone || ''} ${s.externalId || ''}`, query))
      .slice(0, 8);
  }, [entities.students, query]);

  const matchingGroups = useMemo(() => {
    if (!query) return entities.groups.slice(0, 5);
    return entities.groups
      .filter(g => fuzzyContains(g.name, query))
      .slice(0, 8);
  }, [entities.groups, query]);

  const matchingPages = useMemo(() => {
    if (!query) return pages.slice(0, 8);
    return pages.filter(p => fuzzyContains(p.label, query));
  }, [pages, query]);

  const matchingActions = useMemo(() => {
    if (!query) return actions;
    return actions.filter(a => fuzzyContains(a.label, query));
  }, [actions, query]);

  // Flatten sections into a single keyboard-navigable list
  const flatItems = useMemo(() => {
    const items = [];
    if (matchingActions.length) {
      matchingActions.forEach(a => items.push({ section: 'actions', ...a, kind: 'action' }));
    }
    if (matchingPages.length) {
      matchingPages.forEach(p => items.push({ section: 'pages', ...p, kind: 'page' }));
    }
    matchingStudents.forEach(s => {
      const name = `${s.firstName || ''} ${s.lastName || ''}`.trim() || s.name || s.id;
      const basePath = centerType === 'learning' ? '/students' : '/clients';
      items.push({
        section: 'students',
        id: `s-${s.id}`,
        label: name,
        sublabel: s.phone || s.externalId || '',
        icon: Users,
        path: `${basePath}/${s.id}`,
        kind: 'student',
      });
    });
    matchingGroups.forEach(g => {
      items.push({
        section: 'groups',
        id: `g-${g.id}`,
        label: g.name || g.id,
        sublabel: g.teacherName || '',
        icon: Hash,
        path: `/groups/${g.id}`,
        kind: 'group',
      });
    });
    return items;
  }, [matchingActions, matchingPages, matchingStudents, matchingGroups, centerType]);

  useEffect(() => { setActiveIdx(0); }, [query]);

  // Fire search.no_result when the user has typed something but nothing matches.
  // Debounce 600ms so we don't spam on every keystroke.
  useEffect(() => {
    if (!query || flatItems.length > 0) return undefined;
    const id = setTimeout(() => {
      trackEvent('search.no_result', { query, scope: 'palette', length: query.length });
    }, 600);
    return () => clearTimeout(id);
  }, [query, flatItems.length]);
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector('[data-active="true"]');
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [activeIdx, open]);

  const choose = useCallback((item) => {
    if (!item) return;
    trackEvent('palette.select', {
      kind: item.kind,
      id: item.id,
      hasQuery: !!query,
    });
    setOpen(false);
    if (item.path) navigate(item.path);
  }, [navigate, query]);

  const onInputKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(i => Math.min(i + 1, flatItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(i => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(flatItems[activeIdx]);
    }
  };

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  // Group items by section while preserving the keyboard-flat index
  const grouped = SECTION_ORDER.map(section => ({
    section,
    items: flatItems
      .map((item, idx) => ({ item, idx }))
      .filter(({ item }) => item.section === section),
  })).filter(g => g.items.length > 0);

  return ReactDOM.createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('palette.title', 'Quick search & actions')}
      onClick={() => setOpen(false)}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.45)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        zIndex: 1500,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '10vh',
        animation: 'cmdFadeIn 150ms ease-out',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(640px, 92vw)',
          maxHeight: '70vh',
          background: 'var(--surface-raised)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-2xl)',
          border: '1px solid var(--border-light)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          animation: 'cmdSlideIn 200ms var(--ease-emphasized, cubic-bezier(0.34,1.56,0.64,1))',
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3)',
          padding: 'var(--space-3) var(--space-4)',
          borderBottom: '1px solid var(--border-light)',
        }}>
          <Search size={18} aria-hidden="true" color="var(--text-tertiary)" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKey}
            placeholder={t('palette.placeholder', 'Type to search or run a command…')}
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: 'var(--font-size-base)',
              color: 'var(--text-primary)',
            }}
          />
          <kbd style={{
            padding: '2px 6px',
            background: 'var(--bg-tertiary)',
            border: '1px solid var(--border-light)',
            borderRadius: 'var(--radius-xs)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-tertiary)',
            fontFamily: 'var(--font-family-mono)',
          }}>
            Esc
          </kbd>
        </div>

        <div ref={listRef} style={{ overflow: 'auto', flex: 1 }}>
          {grouped.length === 0 && (
            <div style={{
              padding: 'var(--space-6) var(--space-4)',
              textAlign: 'center',
              color: 'var(--text-tertiary)',
              fontSize: 'var(--font-size-sm)',
            }}>
              {t('palette.noResults', 'No results')}
            </div>
          )}
          {grouped.map(group => (
            <div key={group.section}>
              <div style={{
                padding: '8px 16px 4px',
                fontSize: 'var(--font-size-xs)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                fontWeight: 700,
                color: 'var(--text-tertiary)',
              }}>
                {t(`palette.section.${group.section}`, SECTION_LABELS[group.section])}
              </div>
              {group.items.map(({ item, idx }) => {
                const Icon = item.icon || ChevronRight;
                const active = idx === activeIdx;
                return (
                  <button
                    key={item.id}
                    data-active={active}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onClick={() => choose(item)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-3)',
                      padding: '0.625rem var(--space-4)',
                      background: active ? 'var(--primary-50)' : 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'inherit',
                      color: 'var(--text-primary)',
                      fontSize: 'var(--font-size-sm)',
                      borderInlineStart: active ? '3px solid var(--primary-dark)' : '3px solid transparent',
                    }}
                  >
                    <Icon size={16} aria-hidden="true" color="var(--text-secondary)" />
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.label}
                    </span>
                    {item.sublabel && (
                      <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--font-size-xs)', flexShrink: 0 }}>
                        {item.sublabel}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3)',
          padding: '6px var(--space-4)',
          borderTop: '1px solid var(--border-light)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--text-tertiary)',
          background: 'var(--bg-secondary)',
        }}>
          <span><kbd>↑↓</kbd> {t('palette.navigate', 'navigate')}</span>
          <span><kbd>↵</kbd> {t('palette.select', 'select')}</span>
          <span style={{ marginInlineStart: 'auto' }}>
            {t('palette.hint', 'Press')} <kbd>{navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}</kbd>+<kbd>K</kbd> {t('palette.hintAnytime', 'anytime')}
          </span>
        </div>
      </div>

      <style>{`
        @keyframes cmdFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes cmdSlideIn {
          from { transform: translateY(-12px); opacity: 0; }
          to   { transform: translateY(0);     opacity: 1; }
        }
      `}</style>
    </div>,
    document.body
  );
}
